<script setup>
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps({
  liveEvent: {
    type: Object,
    required: true,
  },
  cols: {
    type: Number,
  },
})
</script>

<template>
  <div class="bg-zinc-100 border border-zinc-200 rounded-lg p-1 w-full mx-auto h-full">
    <div v-if="cols <= 2" class="flex border rounded-lg shadow-sm bg-white w-full h-full gap-4 p-4">
      <!-- Left: add to Calendar and Date -->
      <div class="flex flex-col gap-y-1 items-center border-r pr-4 bg-white w-44 text-center">
        <div class="flex flex-col gap-y-1">
          <span class="uppercase text-gray-800"> {{ moment(liveEvent.starts_at).format('MMM') }}</span>
          <span class="text-4xl font-bold leading-tight">{{ moment(liveEvent.starts_at).format('DD') }}</span>
          <span class="text-gray-800">{{ moment(liveEvent.starts_at).format('YYYY') }}</span>
        </div>
        <div class="flex items-end h-8">
            <span class="flex items-center gap-2 text-sm">
              <CalendarDaysIcon class="size-4 my-auto" :class="cols == 2 ? 'hidden' : 'flex'"/>
              {{ __('Add to Calendar') }}
              <ChevronDownIcon class="size-4 my-auto text-gray-600" />
            </span>
        </div>
      </div>
      <!-- Middle: title, store name, images -->
      <div class="flex flex-col gap-y-2 grow w-2/4">
        <div>
            <span
              class="cursor-pointer text-primary-500 sm:text-lg hover:text-primary-600 hover:underline font-bold line-clamp-1"
            >
              {{ liveEvent.title }}
            </span>
          <span class="text-primary-500 hover:underline">
              <span class="text-sm text-gray-700">{{ __('by') }}</span>
              {{ liveEvent.store.name }}
            </span>
          <div v-if="cols > 2" class="flex gap-1 text-sm">
            <CalendarDaysIcon class="size-4 my-auto" />
            {{ __('Add to Calendar') }}
          </div>
        </div>
        <div class="overflow-x-auto">
          <div class="flex gap-2">
            <img
              v-for="image in liveEvent.images"
              :src="image"
              alt="auction-image"
              class="size-16 sm:size-20 aspect-1 object-cover rounded-lg"
            />
          </div>
        </div>

      </div>
      <!-- Right: countdown, button -->
      <div class="flex flex-col justify-between items-end">
        <div>
          <button class="flex w-full text-center justify-center py-2 px-4 border rounded-md text-sm font-medium border-gray-300 text-gray-500">
            {{ __('Preview') }}
          </button>
        </div>
        <div class="hidden sm:flex flex-col font-bold text-gray-600 gap-x-1 items-end">
            <span class="font-normal">
              {{ __('Starts in') }}
            </span>
          <span class="flex gap-1 text-sm">
            <ClockIcon class="size-4 my-auto" :class="cols == 2 ? 'hidden' : 'flex'"/>
            20h 30m 25s
          </span>
        </div>
      </div>
    </div>
    <div v-else class="flex flex-col border shadow-sm bg-white  p-2">
      <!-- Top: image, countdown  -->
      <div class="size-full mx-auto justify-items-center">
        <div class="overflow-hidden">
          <div class="flex touch-pan-y touch-pinch-zoom">
            <div class="w-full flex-shrink-0 min-w-0">
              <img
                :src="liveEvent.images[0] ?? liveEvent.store.image"
                alt="auction-image"
                class="aspect-1 object-cover object-center"
              >
            </div>
          </div>
        </div>
        <div class="absolute inset-x-0 top-0 flex justify-end">
          <div class="flex bg-gray-50/80 px-2 py-1 rounded-lg m-4 w-fit text-sm items-center text-center justify-center">
            <ClockIcon class="size-4 my-auto" />

            20h 30m 25s
          </div>
        </div>
        <div class="absolute flex inset-x-4 items-center justify-between px-2 top-1/3">
          <ChevronLeftIcon
            class="bg-white/70 border rounded-full p-2 !stroke-1 !size-8 shadow"
          />
          <ChevronRightIcon
            class="bg-white/70 border rounded-full p-2 !stroke-1 !size-8 shadow"
          />
        </div>
      </div>
      <!-- Bottom: add to Calendar, date, title, store name -->
      <div class="flex my-2 gap-2 grow">
        <div class="flex flex-col text-center text-sm items-center justify-center border px-3 py-1 rounded">
          <div class="flex flex-col gap-y-1">
            <span class="uppercase text-gray-800"> {{ moment(liveEvent.starts_at).format('MMM') }}</span>
            <span class="text-2xl font-bold leading-tight">{{ moment(liveEvent.starts_at).format('DD') }}</span>
            <span class="text-gray-800">{{ moment(liveEvent.starts_at).format('YYYY') }}</span>
          </div>
        </div>
        <div>
            <span class="text-primary-500 sm:text-lg hover:text-primary-600 hover:underline font-bold line-clamp-1">
              {{ liveEvent.title }}
            </span>
          <span class="text-primary-500 hover:underline">
              <span class="text-sm text-gray-700">{{ __('by') }}</span>
              {{ liveEvent.store.name }}
            </span>
          <div class="flex gap-1 text-sm">
            <CalendarDaysIcon class="size-4 my-auto" />
            {{ __('Add to Calendar') }}
          </div>
        </div>
      </div>
      <!-- Button -->
      <div>
        <button
          class="flex w-full text-center justify-center py-2 px-4 border rounded-md text-sm font-medium border-gray-300 text-gray-500"
        >
          {{ __('Preview') }}
        </button>
      </div>
    </div>
  </div>
</template>
